import { initializeApp } from 'firebase/app'
import { getDatabase, ref, get, onValue, off, set, push, update, serverTimestamp } from 'firebase/database'
import { getAuth, signInAnonymously, onAuthStateChanged, signOut } from 'firebase/auth'

const firebaseConfig = {
    apiKey: "AIzaSyAI7ps1aCmABNpQdcQst4KA7lharZxheys",
    authDomain: "com-wearit-app.firebaseapp.com",
    databaseURL: "https://com-wearit-app-default-rtdb.firebaseio.com",
    projectId: "com-wearit-app",
    storageBucket: "com-wearit-app.firebasestorage.app",
    messagingSenderId: "181542753360",
    appId: "1:181542753360:web:01751095c2a3b2cd012d55"
};
  

// Initialize Firebase exactly like frontend
let app
let db
let auth

try {
    app = initializeApp(firebaseConfig, 'adminDashboard')
    db = getDatabase(app)
    auth = getAuth(app)
} catch (error) {
    // Firebase already initialized
    try {
        app = initializeApp(firebaseConfig)
        db = getDatabase(app)
        auth = getAuth(app)
    } catch (fallbackError) {
        db = null
        auth = null
    }
}

class FirebaseService {
    /**
     * Check if Firebase is properly initialized and connected
     * @returns {Object} Firebase status information
     */
    getFirebaseStatus() {
        const status = {
            initialized: !!db,
            databaseConnected: !!db,
            authConnected: !!auth,
            usingMockData: false,
            usingBackendAPI: false,
            databaseURL: firebaseConfig.databaseURL,
            currentUser: auth?.currentUser ? {
                uid: auth.currentUser.uid,
                isAnonymous: auth.currentUser.isAnonymous
            } : null,
            authState: auth ? 'initialized' : 'not_initialized'
        }
        
        return status;
    }

    /**
     * Initialize Firebase authentication for dashboard
     * @returns {Promise<boolean>} Success status
     */
    async initializeDashboardAuth() {
        try {
            
            if (!auth) {
                return false;
            }

            // Check if already authenticated
            if (auth.currentUser) {
                return true;
            }

            // Try anonymous authentication
            const userCredential = await signInAnonymously(auth);
            
            if (userCredential.user) {
                return true;
            } else {
                return false;
            }
        } catch (error) {
            return false;
        }
    }

    /**
     * Get current Firebase user ID
     * @returns {string|null} Firebase user ID
     */
    getCurrentFirebaseUid() {
        return auth?.currentUser?.uid || null;
    }

    /**
     * Check if user is authenticated with Firebase
     * @returns {boolean} Authentication status
     */
    isAuthenticated() {
        return auth?.currentUser != null;
    }

    /**
     * Get Firebase UID with fallback to anonymous auth
     * @returns {Promise<string|null>} Firebase UID
     */
    async getFirebaseUidWithFallback() {
        // First try to get current Firebase user
        let firebaseUid = auth?.currentUser?.uid;
        
        if (firebaseUid != null) {
            return firebaseUid;
        }

        // If no current user, try anonymous authentication
        try {
            const userCredential = await signInAnonymously(auth);
            firebaseUid = userCredential.user?.uid;
            if (firebaseUid != null) {
                return firebaseUid;
            }
        } catch (e) {
        }

        return null;
    }

    /**
     * Ensure Firebase authentication before database operations
     * @returns {Promise<boolean>} Authentication success
     */
    async ensureAuthentication() {
        
        if (this.isAuthenticated()) {
            return true;
        }

        const authResult = await this.initializeDashboardAuth();
        
        if (authResult) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Listen to all chats in real-time for admin dashboard
     * @param {Function} callback - Callback function to handle chat updates
     * @returns {Function} Unsubscribe function
     */
    async listenToAllChats(callback) {
        try {
            // Ensure authentication before accessing database
            const isAuthenticated = await this.ensureAuthentication();
            if (!isAuthenticated) {
                callback([]);
                return () => {};
            }

            if (!db) {
                throw new Error('Firebase database not initialized')
            }
            
            const chatsRef = ref(db, 'chats')
            
            const unsubscribe = onValue(chatsRef, (snapshot) => {
                try {
                    if (!snapshot.exists()) {
                        callback([])
                        return
                    }

                    const chatsData = []
                    snapshot.forEach((childSnapshot) => {
                        const chatData = childSnapshot.val()
                        const chatId = childSnapshot.key

                        // Parse chat ID to extract participant IDs and product ID
                        const chatParts = chatId.split('_')
                        let participantIds = []
                        let productId = ''

                        if (chatParts.length >= 3) {
                            participantIds = [chatParts[0], chatParts[1]]
                            productId = chatParts.slice(2).join('_') // Handle product IDs with underscores
                        }

                        // Count messages and offers
                        const messageCount = chatData.messages ? Object.keys(chatData.messages).length : 0
                        const offerCount = chatData.offers ? Object.keys(chatData.offers).length : 0

                        // Extract participant information
                        const participants = []
                        if (chatData.participantsInfo) {
                            Object.keys(chatData.participantsInfo).forEach(participantId => {
                                participants.push({
                                    id: participantId,
                                    ...chatData.participantsInfo[participantId]
                                })
                            })
                        }

                        chatsData.push({
                            firebaseId: chatId,
                            id: chatId,
                            participantIds,
                            productId,
                            participants,
                            participantsInfo: chatData.participantsInfo || {},
                            productInfo: chatData.productInfo || {},
                            roles: chatData.roles || {},
                            lastMessage: chatData.lastMessage || 'No messages',
                            lastMessageTimestamp: chatData.lastMessageTimestamp || chatData.createdAt || 0,
                            lastMessageSenderId: chatData.lastMessageSenderId,
                            createdAt: chatData.createdAt || 0,
                            lastUpdated: chatData.lastUpdated || chatData.lastMessageTimestamp || chatData.createdAt || 0,
                            messageCount,
                            offerCount,
                            isFinalized: chatData.isFinalized || false,
                            status: this.getChatStatus(chatData)
                        })
                    })

                    // Sort by last message timestamp (most recent first)
                    const sortedChats = chatsData.sort((a, b) => (b.lastMessageTimestamp || 0) - (a.lastMessageTimestamp || 0))
                    callback(sortedChats)
                } catch (error) {
                    callback([])
                }
            }, (error) => {
                callback([])
            })
            
            return unsubscribe
        } catch (error) {
            throw error
        }
    }

    /**
     * Get all chat conversations from Firebase for admin review
     * @returns {Promise<Array>} Array of chat conversations with full details
     */
    async getAllChats() {
        try {
            // Ensure authentication before accessing database
            const isAuthenticated = await this.ensureAuthentication();
            if (!isAuthenticated) {
                return [];
            }

            if (!db) {
                throw new Error('Firebase database not initialized')
            }
            
            const chatsRef = ref(db, 'chats')
            const snapshot = await get(chatsRef)
            
            if (!snapshot.exists()) {
                return []
            }

            const chatsData = []
            snapshot.forEach((childSnapshot) => {
                const chatData = childSnapshot.val()
                const chatId = childSnapshot.key

                // Parse chat ID to extract participant IDs and product ID
                const chatParts = chatId.split('_')
                let participantIds = []
                let productId = ''

                if (chatParts.length >= 3) {
                    participantIds = [chatParts[0], chatParts[1]]
                    productId = chatParts.slice(2).join('_') // Handle product IDs with underscores
                }

                // Count messages and offers
                const messageCount = chatData.messages ? Object.keys(chatData.messages).length : 0
                const offerCount = chatData.offers ? Object.keys(chatData.offers).length : 0

                // Extract participant information
                const participants = []
                if (chatData.participantsInfo) {
                    Object.keys(chatData.participantsInfo).forEach(participantId => {
                        participants.push({
                            id: participantId,
                            ...chatData.participantsInfo[participantId]
                        })
                    })
                }

                chatsData.push({
                    firebaseId: chatId,
                    id: chatId,
                    participantIds,
                    productId,
                    participants,
                    participantsInfo: chatData.participantsInfo || {},
                    productInfo: chatData.productInfo || {},
                    roles: chatData.roles || {},
                    lastMessage: chatData.lastMessage || 'No messages',
                    lastMessageTimestamp: chatData.lastMessageTimestamp || chatData.createdAt || 0,
                    lastMessageSenderId: chatData.lastMessageSenderId,
                    createdAt: chatData.createdAt || 0,
                    lastUpdated: chatData.lastUpdated || chatData.lastMessageTimestamp || chatData.createdAt || 0,
                    messageCount,
                    offerCount,
                    isFinalized: chatData.isFinalized || false,
                    status: this.getChatStatus(chatData)
                })
            })

            // Sort by last message timestamp (most recent first)
            return chatsData.sort((a, b) => (b.lastMessageTimestamp || 0) - (a.lastMessageTimestamp || 0))
        } catch (error) {
            throw error  // Don't fall back to mock data, let the UI handle the error
        }
    }

    /**
     * Get chat statistics for admin dashboard
     * @returns {Promise<Object>} Chat statistics
     */
    async getChatStatistics() {
        try {
            const chats = await this.getAllChats()

            const totalChats = chats.length
            const activeChats = chats.filter(chat => chat.messageCount > 1).length
            const chatsWithOffers = chats.filter(chat => chat.offerCount > 0).length
            const finalizedChats = chats.filter(chat => chat.isFinalized).length

            // Calculate engagement metrics
            const totalMessages = chats.reduce((sum, chat) => sum + chat.messageCount, 0)
            const totalOffers = chats.reduce((sum, chat) => sum + chat.offerCount, 0)
            const avgMessagesPerChat = totalChats > 0 ? (totalMessages / totalChats).toFixed(1) : 0

            return {
                totalChats,
                activeChats,
                chatsWithOffers,
                finalizedChats,
                totalMessages,
                totalOffers,
                avgMessagesPerChat,
                engagementRate: totalChats > 0 ? ((activeChats / totalChats) * 100).toFixed(1) : 0
            }
        } catch (error) {
            throw error
        }
    }

    /**
     * Get messages for a specific chat
     * @param {string} chatId - Firebase chat ID
     * @returns {Promise<Array>} Array of messages
     */
    async getChatMessages(chatId) {
        try {
            // Ensure authentication before accessing database
            const isAuthenticated = await this.ensureAuthentication();
            if (!isAuthenticated) {
                return [];
            }

            if (!db) {
                throw new Error('Firebase database not initialized')
            }

            const messagesRef = ref(db, `chats/${chatId}/messages`)
            const snapshot = await get(messagesRef)

            if (!snapshot.exists()) {
                return []
            }

            const messages = []
            snapshot.forEach((childSnapshot) => {
                messages.push({
                    id: childSnapshot.key,
                    ...childSnapshot.val()
                })
            })

            // Sort by timestamp
            return messages.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
        } catch (error) {
            throw error
        }
    }

    /**
     * Get offers for a specific chat
     * @param {string} chatId - Firebase chat ID
     * @returns {Promise<Array>} Array of offers
     */
    async getChatOffers(chatId) {
        try {
            // Ensure authentication before accessing database
            const isAuthenticated = await this.ensureAuthentication();
            if (!isAuthenticated) {
                return [];
            }

            if (!db) {
                throw new Error('Firebase database not initialized')
            }

            const offersRef = ref(db, `chats/${chatId}/offers`)
            const snapshot = await get(offersRef)

            if (!snapshot.exists()) {
                return []
            }

            const offers = []
            snapshot.forEach((childSnapshot) => {
                offers.push({
                    id: childSnapshot.key,
                    ...childSnapshot.val()
                })
            })

            // Sort by timestamp
            return offers.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
        } catch (error) {
            throw error
        }
    }

    /**
     * Get complete chat details including messages and offers
     * @param {string} chatId - Firebase chat ID
     * @returns {Promise<Object|null>} Complete chat data
     */
    async getChatDetails(chatId) {
        try {
            // Ensure authentication before accessing database
            const isAuthenticated = await this.ensureAuthentication();
            if (!isAuthenticated) {
                return null;
            }

            if (!db) {
                throw new Error('Firebase database not initialized')
            }

            const chatRef = ref(db, `chats/${chatId}`)
            const snapshot = await get(chatRef)

            if (!snapshot.exists()) {
                return null
            }

            const chatData = snapshot.val()

            // Parse chat ID to extract participant IDs and product ID
            const chatParts = chatId.split('_')
            let participantIds = []
            let productId = ''

            if (chatParts.length >= 3) {
                participantIds = [chatParts[0], chatParts[1]]
                productId = chatParts.slice(2).join('_')
            }

            // Get messages and offers
            const [messages, offers] = await Promise.all([
                this.getChatMessages(chatId),
                this.getChatOffers(chatId)
            ])

            // Extract participant information
            const participants = []
            if (chatData.participantsInfo) {
                Object.keys(chatData.participantsInfo).forEach(participantId => {
                    participants.push({
                        id: participantId,
                        ...chatData.participantsInfo[participantId]
                    })
                })
            }

            const result = {
                firebaseId: chatId,
                id: chatId,
                participantIds,
                productId,
                participants,
                participantsInfo: chatData.participantsInfo || {},
                productInfo: chatData.productInfo || {},
                roles: chatData.roles || {},
                lastMessage: chatData.lastMessage || 'No messages',
                lastMessageTimestamp: chatData.lastMessageTimestamp || chatData.createdAt || 0,
                lastMessageSenderId: chatData.lastMessageSenderId,
                createdAt: chatData.createdAt || 0,
                lastUpdated: chatData.lastUpdated || chatData.lastMessageTimestamp || chatData.createdAt || 0,
                isFinalized: chatData.isFinalized || false,
                messages,
                offers,
                messageCount: messages.length,
                offerCount: offers.length,
                status: this.getChatStatus(chatData)
            }

            return result
        } catch (error) {
            throw error
        }
    }

    /**
     * Listen to messages in real-time for a specific chat
     * @param {string} chatId - Firebase chat ID
     * @param {Function} callback - Callback function to handle messages
     * @returns {Function} Unsubscribe function
     */
    async listenToMessages(chatId, callback) {
        try {
            // Ensure authentication before accessing database
            const isAuthenticated = await this.ensureAuthentication();
            if (!isAuthenticated) {
                callback([]);
                return () => {};
            }

            if (!db) {
                return () => { }
            }

            const messagesRef = ref(db, `chats/${chatId}/messages`)

            const unsubscribe = onValue(messagesRef, (snapshot) => {
                const messages = []
                if (snapshot.exists()) {
                    snapshot.forEach((childSnapshot) => {
                        messages.push({
                            id: childSnapshot.key,
                            ...childSnapshot.val()
                        })
                    })
                }

                // Sort by timestamp
                messages.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
                callback(messages)
            }, (error) => {
                callback([])
            })

            return () => off(messagesRef)
        } catch (error) {
            return () => { }
        }
    }

    /**
     * Listen to offers in real-time for a specific chat
     * @param {string} chatId - Firebase chat ID
     * @param {Function} callback - Callback function to handle offers
     * @returns {Function} Unsubscribe function
     */
    async listenToOffers(chatId, callback) {
        try {
            // Ensure authentication before accessing database
            const isAuthenticated = await this.ensureAuthentication();
            if (!isAuthenticated) {
                callback([]);
                return () => {};
            }

            if (!db) {
                return () => { }
            }

            const offersRef = ref(db, `chats/${chatId}/offers`)

            const unsubscribe = onValue(offersRef, (snapshot) => {
                const offers = []
                if (snapshot.exists()) {
                    snapshot.forEach((childSnapshot) => {
                        offers.push({
                            id: childSnapshot.key,
                            ...childSnapshot.val()
                        })
                    })
                }

                // Sort by timestamp
                offers.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
                callback(offers)
            }, (error) => {
                callback([])
            })

            return () => off(offersRef)
        } catch (error) {
            return () => { }
        }
    }

    /**
     * Format timestamp for display
     * @param {number} timestamp - Unix timestamp
     * @returns {string} Formatted timestamp
     */
    formatTimestamp(timestamp) {
        if (!timestamp) return 'Never'

        const date = new Date(timestamp)
        const now = new Date()
        const diff = now - date

        // Less than a minute
        if (diff < 60 * 1000) {
            return 'Just now'
        }

        // Less than an hour
        if (diff < 60 * 60 * 1000) {
            const minutes = Math.floor(diff / (60 * 1000))
            return `${minutes}m ago`
        }

        // Less than a day
        if (diff < 24 * 60 * 60 * 1000) {
            const hours = Math.floor(diff / (60 * 60 * 1000))
            return `${hours}h ago`
        }

        // Less than a week
        if (diff < 7 * 24 * 60 * 60 * 1000) {
            const days = Math.floor(diff / (24 * 60 * 60 * 1000))
            return `${days}d ago`
        }

        // Format as date
        return date.toLocaleDateString()
    }

    /**
     * Determine chat status based on chat data
     * @param {Object} chatData - Firebase chat data
     * @returns {string} Chat status
     */
    getChatStatus(chatData) {
        // Check if chat is blocked first
        if (chatData.status === 'blocked') {
            return 'Blocked'
        }

        if (chatData.isFinalized) {
            return 'Completed'
        }

        if (chatData.offers) {
            const offers = Object.values(chatData.offers)
            const hasAcceptedOffer = offers.some(offer => offer.status === 'accepted')
            const hasPendingOffer = offers.some(offer => offer.status === 'pending')

            if (hasAcceptedOffer) {
                return 'Offer Accepted'
            }

            if (hasPendingOffer) {
                return 'Pending Offer'
            }
        }

        const messageCount = chatData.messages ? Object.keys(chatData.messages).length : 0

        if (messageCount === 0) {
            return 'New'
        } else if (messageCount === 1) {
            return 'Started'
        } else {
            return 'Active'
        }
    }

    /**
     * Block a chat (admin only)
     * @param {string} chatId - Chat ID to block
     * @param {string} adminId - Admin ID who is blocking
     * @param {string} reason - Reason for blocking
     * @returns {Promise<void>}
     */
    async blockChat(chatId, adminId, reason = '') {
        try {
            
            // Ensure authentication before accessing database
            const isAuthenticated = await this.ensureAuthentication();
            
            if (!isAuthenticated) {
                throw new Error('Authentication required');
            }

            if (!db) {
                throw new Error('Firebase database not initialized')
            }

            const chatRef = ref(db, `chats/${chatId}`)
            
            // Update chat status to blocked
            await update(chatRef, {
                status: 'blocked',
                blockedAt: serverTimestamp(),
                blockedBy: adminId,
                blockReason: reason,
                lastUpdated: serverTimestamp()
            })

        } catch (error) {
            throw error
        }
    }

    /**
     * Unblock a chat (admin only)
     * @param {string} chatId - Chat ID to unblock
     * @param {string} adminId - Admin ID who is unblocking
     * @returns {Promise<void>}
     */
    async unblockChat(chatId, adminId) {
        try {
            // Ensure authentication before accessing database
            const isAuthenticated = await this.ensureAuthentication();
            if (!isAuthenticated) {
                throw new Error('Authentication required');
            }

            if (!db) {
                throw new Error('Firebase database not initialized')
            }

            const chatRef = ref(db, `chats/${chatId}`)

            // Remove blocked status and related fields
            await update(chatRef, {
                status: null,
                blockedAt: null,
                blockedBy: null,
                blockReason: null,
                lastUpdated: serverTimestamp()
            })

        } catch (error) {
            throw error
        }
    }

    /**
     * Assign a conversation to a staff member (admin only) — same chat-root
     * metadata mechanism as blockChat. Pass staff=null to unassign.
     * @param {string} chatId
     * @param {{id: string, name: string}|null} staff
     * @param {string} adminId - who performed the assignment
     */
    async assignChat(chatId, staff, adminId) {
        try {
            const isAuthenticated = await this.ensureAuthentication()
            if (!isAuthenticated) {
                throw new Error('Authentication required')
            }
            if (!db) {
                throw new Error('Firebase database not initialized')
            }

            const chatRef = ref(db, `chats/${chatId}`)
            await update(chatRef, {
                assignedTo: staff ? { id: staff.id, name: staff.name } : null,
                assignedAt: staff ? serverTimestamp() : null,
                assignedBy: staff ? adminId : null,
                lastUpdated: serverTimestamp(),
            })
        } catch (error) {
            throw error
        }
    }

    _normalizeTimestamp(value) {
        if (typeof value === 'number') return value
        if (value && typeof value === 'object' && typeof value.seconds === 'number') {
            return value.seconds * 1000
        }
        return Date.now()
    }

    async _getChatContext(chatId) {
        if (!db) {
            throw new Error('Firebase database not initialized')
        }

        const chatRef = ref(db, `chats/${chatId}`)
        const snapshot = await get(chatRef)

        if (!snapshot.exists()) {
            throw new Error('Chat not found')
        }

        const chatData = snapshot.val() || {}
        const participantsInfo = chatData.participantsInfo || {}
        const roles = chatData.roles || {}
        const buyerId = roles.buyer || Object.keys(participantsInfo).find((id) => id !== 'support') || ''
        const buyerInfo = participantsInfo[buyerId] || {}
        const supportInfo = participantsInfo.support || {}

        return {
            chatRef,
            chatData,
            participantsInfo,
            roles,
            buyerId,
            buyerInfo,
            supportName: supportInfo.name || 'WearIt',
            buyerName: buyerInfo.name || buyerInfo.username || 'Buyer',
            productInfo: chatData.productInfo || {},
            productId: chatData.productId || chatData.productInfo?.id || ''
        }
    }

    async sendAdminMessage(chatId, { message, adminActorId = '', senderName = 'WearIt' } = {}) {
        try {
            const isAuthenticated = await this.ensureAuthentication();
            if (!isAuthenticated) {
                throw new Error('Authentication required');
            }

            if (!message || !message.trim()) {
                throw new Error('Message is required');
            }

            const { chatRef, buyerId, buyerName } = await this._getChatContext(chatId)
            const timestamp = Date.now()
            const messagesRef = ref(db, `chats/${chatId}/messages`)
            const newMessageRef = push(messagesRef)
            const messageId = newMessageRef.key
            const cleanMessage = message.trim()

            await set(newMessageRef, {
                id: messageId,
                senderId: 'support',
                senderName,
                receiverId: buyerId,
                receiverName: buyerName,
                message: cleanMessage,
                timestamp,
                type: 'message',
                read: false,
                adminActorId,
            })

            await update(chatRef, {
                lastMessage: cleanMessage,
                lastMessageSenderId: 'support',
                lastMessageTimestamp: timestamp,
                lastUpdated: timestamp,
            })

            await update(ref(db, `chats/${chatId}/participantsInfo/support`), {
                id: 'support',
                name: senderName,
                role: 'support',
            })
        } catch (error) {
            throw error
        }
    }

    async updateOfferStatus(chatId, offerId, status, adminActorId = '') {
        try {
            const isAuthenticated = await this.ensureAuthentication();
            if (!isAuthenticated) {
                throw new Error('Authentication required');
            }

            const timestamp = Date.now()
            await update(ref(db, `chats/${chatId}/offers/${offerId}`), {
                status,
                updatedAt: timestamp,
                adminActorId,
            })
            await update(ref(db, `chats/${chatId}/messages/${offerId}`), {
                status,
                updatedAt: timestamp,
                adminActorId,
            })
            await update(ref(db, `chats/${chatId}`), {
                lastUpdated: timestamp,
            })
        } catch (error) {
            throw error
        }
    }

    async counterOffer(chatId, offerId, amount, { adminActorId = '', senderName = 'WearIt' } = {}) {
        try {
            const isAuthenticated = await this.ensureAuthentication();
            if (!isAuthenticated) {
                throw new Error('Authentication required');
            }

            const numericAmount = Number(amount)
            if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
                throw new Error('Valid counter offer amount is required')
            }

            const { chatRef, buyerId, buyerName, productInfo, productId, supportName } = await this._getChatContext(chatId)
            const existingOfferRef = ref(db, `chats/${chatId}/offers/${offerId}`)
            const existingOfferSnapshot = await get(existingOfferRef)

            if (!existingOfferSnapshot.exists()) {
                throw new Error('Original offer not found')
            }

            const existingOffer = existingOfferSnapshot.val() || {}
            const timestamp = Date.now()
            const offerCurrency = existingOffer.currency || 'AED'
            const exchangeRate = existingOffer.exchangeRate || 1
            const amountInSelectedCurrency = numericAmount
            const formattedAmount = `${offerCurrency} ${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amountInSelectedCurrency)}`

            await update(existingOfferRef, {
                status: 'countered',
                updatedAt: timestamp,
                adminActorId,
            })
            await update(ref(db, `chats/${chatId}/messages/${offerId}`), {
                status: 'countered',
                updatedAt: timestamp,
                adminActorId,
            })

            const newOfferRef = push(ref(db, `chats/${chatId}/offers`))
            const newOfferId = newOfferRef.key
            const offerPayload = {
                id: newOfferId,
                senderId: 'support',
                senderName: senderName || supportName,
                receiverId: buyerId,
                receiverName: buyerName,
                amount: numericAmount,
                timestamp,
                status: 'pending',
                productId: existingOffer.productId || productId || '',
                productName: existingOffer.productName || productInfo.name || '',
                productImage: existingOffer.productImage || productInfo.image || '',
                metadata: {
                    ...(existingOffer.metadata || {}),
                    isCounterOffer: true,
                    originalOfferId: offerId,
                    adminActorId,
                },
                currency: offerCurrency,
                defaultCurrency: existingOffer.defaultCurrency || 'AED',
                exchangeRate,
                amountInSelectedCurrency,
                formattedAmount,
            }

            await set(newOfferRef, offerPayload)
            await set(ref(db, `chats/${chatId}/messages/${newOfferId}`), {
                id: newOfferId,
                type: 'offer',
                message: `I'd like to offer for "this item"`,
                senderId: 'support',
                senderName: senderName || supportName,
                receiverId: buyerId,
                receiverName: buyerName,
                timestamp,
                read: false,
                offerId: newOfferId,
                offerAmount: numericAmount,
                status: 'pending',
                amountInSelectedCurrency,
                formattedAmount,
                currency: offerCurrency,
                defaultCurrency: existingOffer.defaultCurrency || 'AED',
                exchangeRate,
                productId: existingOffer.productId || productId || '',
                productName: existingOffer.productName || productInfo.name || '',
                productImage: existingOffer.productImage || productInfo.image || '',
                adminActorId,
            })

            await update(chatRef, {
                lastMessage: `I'd like to offer for "this item"`,
                lastMessageSenderId: 'support',
                lastMessageTimestamp: timestamp,
                lastUpdated: timestamp,
            })

            return newOfferId
        } catch (error) {
            throw error
        }
    }

    /**
     * Generate Firebase chat ID from participants and product
     * @param {Array} participantIds - Array of participant IDs
     * @param {string} productId - Product ID
     * @returns {string} Firebase chat ID
     */
    generateChatId(participantIds, productId) {
        const sortedParticipants = [...participantIds].sort()
        return `${sortedParticipants[0]}_${sortedParticipants[1]}_${productId}`
    }

    /**
     * Sign out from Firebase
     */
    async signOut() {
        try {
            if (auth) {
                await signOut(auth);
            }
        } catch (error) {
            throw error;
        }
    }

    // All mock data methods removed - only using real Firebase data
}

// Initialize dashboard authentication when service is loaded
const firebaseService = new FirebaseService()
firebaseService.initializeDashboardAuth().then(success => {
    if (success) {
    } else {
    }
}).catch(error => {
});

export default firebaseService 